import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Button,
  LoadingButton,
  Link,
} from '@one-thd/sui-atomic-components';
import { CityStateProps, ContactDetailsFormProps } from '@src/models/lorp';
import { TagOnCompleteContactInformationForm } from '@src/utils/analytics-tagging';
import { getFeatureSwitchStatic, scrollToTop } from '@src/utils/general';
import {
  areContactDetailsValidated,
  getEditableContactFields,
  initialRequiredFieldsStatus,
  mapContactDetailsFormToContactDetails,
  mapContactDetailsToForm,
} from '@src/utils/lorpContactDetailsUtil';
import useFetchState from '@src/hooks/useFetchState';
import {
  IValidateAddressRequest,
  IValidateAddressResponse,
  IValidateZipCodeRequest,
  IValidateZipCodeResponse,
} from '@src/models/api';
import { useReturnStore } from '@src/stores/returnStore';
import { IErrorReturnService } from '@src/models/errors';
import {
  handleAddressValidateCall,
  handleZipCodeValidateCall,
} from '@src/utils/api-helpers';
import { ILORPContactDetails } from './LORPContactDetailsStep';
import { LORPContactForm } from './LORPContactForm';
import { LORPContactFormAlerts } from './LORPContactFormAlerts';
import { LORPAddressSuggestionModal } from './LORPAddressSuggestionModal';

export interface ContactDetailsDrawerProps {
  displayDrawer: boolean;
  closeDrawer: () => void;
  contactDetails?: ILORPContactDetails;
  setContactDetails: (value: ILORPContactDetails) => void;
  isSTH?: boolean;
}

export const ContactDetailsDrawer = ({
  displayDrawer,
  closeDrawer,
  contactDetails = {},
  setContactDetails,
  isSTH,
}: ContactDetailsDrawerProps) => {
  const orderDetailsV2 = useReturnStore((state) => state.orderDetailsV2);
  const drawerRef = useRef<HTMLDivElement>(null);
  const [tempContactDetails, setTempContactDetails] = useState<
    ContactDetailsFormProps | undefined
  >();
  const [requiredFieldsStatus, setRequiredFieldsStatus] = useState(
    initialRequiredFieldsStatus,
  );
  const [displayAddressSuggestionModal, setDisplayAddressSuggestionModal] =
    useState<boolean>(false);
  // We want to have it as false when the modal is reopened
  const [isAddressValidated, setIsAddressValidated] = useState<boolean>(false);
  const [suggestedAddress, setSuggestedAddress] = useState<
    ContactDetailsFormProps | undefined
  >();
  const [availableCityStates, setAvailableCityStates] =
    useState<CityStateProps[]>();
  const [isZipcodeValidating, setIsZipcodeValidating] = useState(false);
  const [confirmAddressState, setConfirmAddressState] = useState<
    'unconfirmed' | 'invalid'
  >('invalid');
  const isNetTokenEnabled = getFeatureSwitchStatic(
    'isValidationNetTokenEnabled',
    true,
    false,
  );

  const {
    loading: validateAddressLoading,
    fetchData: validateAddressFetch,
    error: addressValidationErrorResponse,
  } = useFetchState<IValidateAddressResponse>();
  const { fetchData: validateZipCodeFetch, error: zipCodeValidationError } =
    useFetchState<IValidateZipCodeResponse>();
  // TODO check up on this looping error
  useEffect(() => {
    mapContactDetailsToForm(setTempContactDetails, contactDetails);
    if (isSTH) {
      setAvailableCityStates([
        {
          city: contactDetails.address?.city,
          stateCode: contactDetails.address?.state,
        },
      ]);
    }
  }, [
    contactDetails?.firstName,
    contactDetails?.lastName,
    contactDetails?.address?.addressLine1,
    contactDetails?.address?.addressLine2,
    contactDetails?.address?.addressLine3,
    contactDetails?.address?.city,
    contactDetails?.address?.state,
    contactDetails?.address?.zipCode,
    isSTH,
  ]);

  useEffect(() => {
    if (isAddressValidated) {
      setDisplayAddressSuggestionModal(false);
      handleSubmit();
    }
  }, [isAddressValidated]);

  useEffect(() => {
    const errorResponse = addressValidationErrorResponse as IErrorReturnService;
    if (
      errorResponse &&
      errorResponse.code === 'VALIDATE_ADDRESS_REQUEST_BAD_INPUT_ADDRESS'
    ) {
      // if the error returned is ADDRESS_UNCONFIRMED, we let the user confirm the address
      setConfirmAddressState('unconfirmed');
    }
  }, [addressValidationErrorResponse, validateAddressLoading]);

  const cancelDrawer = () => {
    // Reset the temporary contact details to the original values, cant use unmount since component is technically never unmounted
    mapContactDetailsToForm(setTempContactDetails, contactDetails);
    // Reset the required fields status to true
    setRequiredFieldsStatus(initialRequiredFieldsStatus);
    setIsAddressValidated(false);
    setConfirmAddressState('invalid');
    closeDrawer();
  };

  const clearEditableFields = () => {
    const editableFields = getEditableContactFields(isSTH);
    setTempContactDetails((prev) => ({
      ...prev,
      ...editableFields,
    }));
  };

  const handleCityStateChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const city = event.target.value;
    setTempContactDetails((prev) => ({
      ...prev,
      city,
    }));
  };

  const handleZipCodeUpdate = async (zipCodeValue: string) => {
    setIsZipcodeValidating(true);
    const zipCodeValidationRequest: IValidateZipCodeRequest = {
      orderId: orderDetailsV2?.orderNumber || '',
      zipCode: zipCodeValue,
      ...(isNetTokenEnabled && {
        net: orderDetailsV2?.net,
        emailId: orderDetailsV2?.emailId,
      }),
    };
    const { data: validationResponse, error } = await validateZipCodeFetch(() =>
      handleZipCodeValidateCall(zipCodeValidationRequest),
    );
    if (error) {
      // Scroll to top of the drawer for network errors
      scrollToTop(drawerRef);
      setIsZipcodeValidating(false);
      return;
    }
    // Throw error if city list is not from same state
    const sameStateCities = validationResponse?.cities?.filter(
      (city) => city.stateCode === contactDetails.address?.state,
    );
    const areZipCodeCitiesValid = sameStateCities && sameStateCities.length > 0;
    if (!areZipCodeCitiesValid) {
      setRequiredFieldsStatus((prev) => ({
        ...prev,
        zipCode: false,
      }));
      setIsZipcodeValidating(false);
      return;
    }

    setAvailableCityStates(sameStateCities);
    setTempContactDetails((prev) => ({
      ...prev,
      city: sameStateCities?.[0]?.city,
      state: sameStateCities?.[0]?.stateCode,
    }));
    setIsZipcodeValidating(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    let output = value;

    // Prevent leading space for all address/contact fields
    if (output.length === 1 && output[0] === ' ') {
      return;
    }

    // check if key exists in requiredFields status and remove any errors if field is no longer empty
    const isRequiredField = Object.keys(requiredFieldsStatus).includes(name);
    if (isRequiredField && value !== '') {
      setRequiredFieldsStatus((prev) => ({
        ...prev,
        [name]: true,
      }));
    }
    // Format zip codes
    if (name === 'zipCode') {
      const digits = value.replace(/\D/g, '');
      if (digits.length > 5) return; // Ignore anything over 5 digits
      // Run zip code validation if the zip code is 5 digits
      setConfirmAddressState('invalid');
      if (digits.length === 5) handleZipCodeUpdate(digits);

      setRequiredFieldsStatus((prev) => ({
        ...prev,
        [name]: true,
      }));

      output = digits;
    }

    if (name === 'addressLine1' || name === 'addressLine2') {
      setConfirmAddressState('invalid');
    }
    setTempContactDetails((prev) => ({
      ...prev,
      [name]: output,
    }));
  };

  // Trim whitespace on blur for all address/contact fields
  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const trimmed = value.trim();
    if (value !== trimmed) {
      setTempContactDetails((prev) => ({
        ...prev,
        [name]: trimmed,
      }));
    }
  };

  const handleAddressValidation = async () => {
    const addressValidationRequest: IValidateAddressRequest = {
      orderId: orderDetailsV2?.orderNumber || '',
      address: {
        addressLine1: tempContactDetails?.addressLine1 || '',
        addressLine2: tempContactDetails?.addressLine2 || undefined,
        city: tempContactDetails?.city || '',
        state: tempContactDetails?.state || '',
        zipCode: tempContactDetails?.zipCode || '',
        country: contactDetails.address?.country || '',
      },
      ...(isNetTokenEnabled && {
        net: orderDetailsV2?.net,
        emailId: orderDetailsV2?.emailId,
      }),
    };
    const { data: validationResponse, error } = await validateAddressFetch(() =>
      handleAddressValidateCall(addressValidationRequest),
    );
    // Network Errors will be handled in the LORPContactFormAlerts component
    // isValid true, no suggestions - Skip modal
    // isValid true, has suggestions - Display modal
    // isValid false, no suggestions - Show error message
    // isValid false, has suggestions - Display modal
    if (error) {
      // Move drawer to top of the screen for network errors
      scrollToTop(drawerRef);
      return;
    }
    const { suggestedAddress: suggestionAddress, isAddressValid } =
      validationResponse || {};
    if (isAddressValid) {
      // Skip modal if address is valid with no suggestions
      if (!suggestionAddress) {
        setIsAddressValidated(true);
        return;
      }
      // Display the recommended address even if the address was valid
      setSuggestedAddress(suggestionAddress);
      setDisplayAddressSuggestionModal(true);
      return;
    }
    // Address is not valid and has no suggestions, so address lines will be alerted and LORPContactFormAlerts will handle the error messaging
    if (!suggestionAddress) {
      scrollToTop(drawerRef);
      setRequiredFieldsStatus((prev) => ({
        ...prev,
        addressLine1: false,
      }));
      return;
    }
    // Display the recommended address if the address is not valid
    setSuggestedAddress(suggestionAddress);
    setDisplayAddressSuggestionModal(true);
  };

  const handleSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();
    // If any required fields are empty we prevent the form from submitting and raise an error for the field
    if (
      !areContactDetailsValidated(
        requiredFieldsStatus,
        setRequiredFieldsStatus,
        tempContactDetails,
      )
    )
      return;

    if (isZipcodeValidating) {
      // If the zip code is validating we prevent the form from submitting
      return;
    }

    // Once all required fields are filled we validate address using the API if the address has not already been validated
    if (!isSTH && !isAddressValidated) {
      handleAddressValidation();
      return;
    }
    onSuccessfulSubmit();
  };

  const onSuccessfulSubmit = () => {
    // Remapping the form fields back to the original structure
    const updatedContactDetails = mapContactDetailsFormToContactDetails(
      contactDetails,
      tempContactDetails,
    );
    // Analytics for successful contact details update
    TagOnCompleteContactInformationForm();
    setContactDetails(updatedContactDetails);
    // Resetting validation flag, cant reset onMount since drawer is never unmounted
    setIsAddressValidated(false);
    closeDrawer();
  };

  const onContinueSuggestionModal = (selectedAddress: string) => {
    if (selectedAddress === 'suggested') {
      setTempContactDetails((prev) => ({
        ...prev,
        ...suggestedAddress,
      }));
    }
    setIsAddressValidated(true);
  };

  return (
    <div className="sui-flex sui-flex-col sui-full sui-gap-4 sui-w-96">
      <Drawer
        open={displayDrawer}
        onClose={cancelDrawer}
        position="right"
        DrawerRootProps={{
          ref: drawerRef,
        }}
        className="drawer-z-index-fix"
      >
        <DrawerHeader
          title="Pickup Address"
          onClose={cancelDrawer}
          data-testid="Pickup-Address"
        />
        <DrawerBody>
          <div className="sui-mb-6">
            <LORPContactFormAlerts
              isSTH={isSTH}
              stateCode={contactDetails.address?.state}
              addressValidationErrorResponse={addressValidationErrorResponse}
              zipCodeValidationError={zipCodeValidationError}
              unconfirmedAddress={confirmAddressState === 'unconfirmed'}
            />
          </div>
          <div className="sui-flex sui-justify-end">
            <Link onClick={clearEditableFields}>Clear All</Link>
          </div>
          <LORPContactForm
            tempContactDetails={tempContactDetails}
            requiredFieldsStatus={requiredFieldsStatus}
            isSTH={isSTH}
            handleInputChange={handleInputChange}
            availableCityStates={availableCityStates}
            handleCityStateChange={handleCityStateChange}
            handleSubmit={handleSubmit}
            handleInputBlur={handleInputBlur}
            unconfirmedAddress={confirmAddressState === 'unconfirmed'}
          />
        </DrawerBody>
        <DrawerFooter className="sui-bg-primary sui-p-4 sui-relative sui-bottom-0 sui-sticky sui-shadow-sm">
          <div className="sui-flex sui-gap-2 sui-flex-col">
            {confirmAddressState === 'unconfirmed' ? (
              <Button fullWidth onClick={onSuccessfulSubmit}>
                Confirm & Continue
              </Button>
            ) : (
              <LoadingButton
                fullWidth
                onClick={handleSubmit}
                loading={validateAddressLoading}
              >
                Update
              </LoadingButton>
            )}

            <Button fullWidth variant="secondary" onClick={cancelDrawer}>
              Cancel
            </Button>
          </div>
        </DrawerFooter>
      </Drawer>
      <LORPAddressSuggestionModal
        displayModal={displayAddressSuggestionModal}
        onClose={() => setDisplayAddressSuggestionModal(false)}
        onContinue={onContinueSuggestionModal}
        formDetails={tempContactDetails}
        suggestedAddress={suggestedAddress}
      />
    </div>
  );
};
